/* eslint-disable react/no-danger, jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    artikelGruppenByNrSelector,
    betreuerByKzSelector,
    branchenByNrSelector,
    einheitenByCodeSelector,
    erweiterteSuchenByNrSelector,
    interesseFuerProdukteByNrSelector,
    laenderByIkz2Selector,
    lookupsLoadingFinishedSelector,
} from 'store/lookups/selector';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { getFocusStyle, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useKunde } from 'api/kunde';
import usePersistentState from 'hooks/usePersistentState';
import { formatDecimalTime, formatMoney, formatSmDate } from 'utils/formatter';
import { getTheme, IconButton } from '@fluentui/react';
import classnames from 'classnames';
import { formStyles } from 'styles/formStyles';
import { CheckboxCompositeIcon, CheckboxIcon } from '@fluentui/react-icons';
import moment from 'moment-timezone';
import { AnredeLabel, KundeArtLabel, TerminArtLabel } from 'utils/labels';
import nl2br from 'react-nl2br';
import { useBetreuerByNr } from 'api/betreuer';

const theme: ITheme = getTheme();
const { palette, semanticColors } = theme;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    positionenTable: {
        width: '100%',
    },
    growTable: {
        maxWidth: '100%',
    },
    group: {
        position: 'relative',
        width: '100%',
    },
    counters: {
        position: 'absolute',
        right: 10,
        top: 10,
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        cursor: 'pointer',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdGroupHeader: {
        fontWeight: 700,
    },
    terminItemCell: [
        getFocusStyle(theme, { inset: -1 }),
        {
            minHeight: 54,
            paddingTop: 5,
            paddingBottom: 5,
            boxSizing: 'border-box',
            borderBottom: `1px solid ${semanticColors.bodyDivider}`,
            cursor: 'pointer',
            selectors: {
                '&:hover': { background: palette.neutralLight },
            },
        },
    ],
    terminStatus: {
        position: 'absolute',
        right: 12,
    },
    terminKontakt: {
        fontWeight: 700,
        width: '50%',
        display: 'inline-block',
    },
    terminZeit: {
        fontWeight: 700,
        width: '50%',
        display: 'inline-block',
    },
    terminBetreff: {
        fontWeight: 700,
        marginTop: 5,
    },
    terminText: {},
    box: {},
    archiv: {
        backgroundColor: '#F7BDBC',
    },
    line: {
        marginTop: 5,
        // marginLeft: 11,
    },
    label: {
        width: 110,
        display: 'inline-block',
    },
    value: {
        display: 'inline-block',
        fontWeight: 700,
    },
    leftDetails: {
        marginTop: 10,
        display: 'inline-block',
        verticalAlign: 'top',
        width: '50%',
    },
    rightDetails: {
        marginTop: 10,
        display: 'inline-block',
        verticalAlign: 'top',
        width: '50%',
    },
    valueList: {
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
    },
    umsaetze: {
        display: 'grid',
        gridTemplateColumns: '110px repeat(4, 1fr)',
    },
    note: {
        // margin: 11,
    },
    title: {
        width: '100%',
        backgroundColor: palette.neutralLight,
        padding: 5,
    },
});

interface KundeSummaryProps {
    kNr: number;
    printView?: boolean;
}

export const KundeSummary: React.FC<KundeSummaryProps> = ({ kNr, printView }) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const betreuerByNr = useBetreuerByNr();
    const betreuerByKz = useSelector(betreuerByKzSelector);
    const branchenByNr = useSelector(branchenByNrSelector);
    const interesseFuerProdukteByNr = useSelector(interesseFuerProdukteByNrSelector);
    const erweiterteSuchenByNr = useSelector(erweiterteSuchenByNrSelector);
    const landIkz2 = useSelector(laenderByIkz2Selector);

    const [angeboteVisibleCount, setAngeboteVisibleCount] = usePersistentState(5, 'KundeSummary.angeboteVisibleCount');
    const [auftraegeVisibleCount, setAuftraegeVisibleCount] = usePersistentState(
        5,
        'KundeSummary.auftraegeVisibleCount',
    );
    const [rechnungenVisibleCount, setRechnungenVisibleCount] = usePersistentState(
        5,
        'KundeSummary.rechnungenVisibleCount',
    );
    const [termineVisibleCount, setTermineVisibleCount] = usePersistentState(5, 'KundeSummary.terminVisibleCount');

    const kunde = useKunde({
        nr: kNr,
        includeArchiv: true,
        withTaskBetreuerNrList: true,
        withAnsprechpartnerList: true,
        withMaschinenkundeList: true,
        withAngebotList: true,
        withAngebotPositionList: true,
        withAuftragList: true,
        withAuftragPositionList: true,
        withRechnungList: true,
        withRechnungPositionList: true,
        withTerminList: true,
        withBrancheList: true,
        withProduktgruppeList: false,
        withErweiterteSucheList: true,
        withInteresseFuerProdukteList: true,
        withMahnstufen: true,
    });

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    const renderCounters = (totalCount: number, visibleCount: number, setCounter: (newValue: number) => void) => {
        const visible = Math.min(totalCount, visibleCount);
        return (
            <div className={styles.counters}>
                {visible}
                {' / '}
                {totalCount}
                <IconButton
                    className="no-print"
                    onClick={() => {
                        setCounter(Math.max(visibleCount - 5, 0));
                    }}
                    disabled={visibleCount <= 0}
                    iconProps={{ iconName: 'CalculatorSubtract', style: { fontSize: 10 } }}
                    style={{ left: 5, top: 1, width: 18, height: 18 }}
                />
                <IconButton
                    className="no-print"
                    onClick={() => {
                        if (visibleCount < totalCount) {
                            setCounter(visibleCount + 5);
                        }
                    }}
                    disabled={visibleCount >= totalCount}
                    iconProps={{ iconName: 'CalculatorAddition', style: { fontSize: 10 } }}
                    style={{ left: 5, top: 1, width: 18, height: 18 }}
                />
            </div>
        );
    };

    const moneyHeadClassName = classnames(
        styles.tdHead,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const moneyBodyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const generateLabelValueLine = (
        label: string,
        value: string | number | null | undefined,
        render?: (value: string | number) => JSX.Element,
    ): JSX.Element | null => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <div className={styles.line}>
                <div className={styles.label}>{label}</div>
                <div className={styles.value}>{render ? render(value) : value}</div>
            </div>
        );
    };

    const renderHead = () => {
        if (!kunde.data) {
            return <div />;
        }

        const thisYear = parseInt(moment().format('YYYY'), 10);

        let websiteUrl = '';
        if (kunde.data.www) {
            websiteUrl =
                kunde.data.www.startsWith('http://') || kunde.data.www.startsWith('https://')
                    ? kunde.data.www
                    : 'http://' + kunde.data.www;
        }

        return (
            <div
                className={classnames(styles.box, { [styles.archiv]: kunde.data.archiv })}
                style={{ paddingBottom: 10 }}
            >
                <div className={styles.leftDetails}>
                    {kunde.data.archiv && (
                        <div className={styles.line}>
                            <div className={styles.label}>
                                <strong>Archiviert!</strong>
                            </div>
                        </div>
                    )}
                    {generateLabelValueLine(
                        'K-Art: ',
                        kunde.data.art ? KundeArtLabel[kunde.data.art] || kunde.data.art : '',
                    )}
                    {generateLabelValueLine('Name 1: ', kunde.data.name1)}
                    {generateLabelValueLine('Name 2: ', kunde.data.name2)}
                    {generateLabelValueLine('K-Nr: ', kunde.data.nr)}
                    {kunde.data.konzernNr
                        ? generateLabelValueLine(
                              'Konzern: ',
                              kunde.data.konzernName1 + ' (' + kunde.data.konzernNr + ')',
                          )
                        : ''}
                    {generateLabelValueLine(
                        'Betreuer: ',
                        betreuerByNr[kunde.data.betreuer]?.name || kunde.data.betreuer,
                    )}
                    {generateLabelValueLine(
                        'Tel-Betreuer: ',
                        betreuerByNr[kunde.data.telBetreuer]?.name || kunde.data.telBetreuer,
                    )}
                </div>
                <div className={styles.rightDetails}>
                    {generateLabelValueLine('Postfach: ', kunde.data.postfach)}
                    {generateLabelValueLine('Straße: ', kunde.data.strasse)}
                    {generateLabelValueLine('PLZ/Ort: ', kunde.data.plz + ' ' + kunde.data.ort)}
                    {generateLabelValueLine(
                        'Land: ',
                        kunde.data.land ? landIkz2[kunde.data.land]?.text || kunde.data.land : '',
                    )}
                    {generateLabelValueLine('Telefon: ', kunde.data.telefon, (value) => (
                        <a href={('tel: ' + value) as string}>{value}</a>
                    ))}
                    {generateLabelValueLine('Fax: ', kunde.data.fax)}
                    {generateLabelValueLine('Mail: ', kunde.data.mail, (value) => (
                        <a href={'mailto: ' + value}>{value}</a>
                    ))}
                    {generateLabelValueLine('Webseite: ', kunde.data.www, (value) => (
                        <a target="_blank" rel="noreferrer" href={websiteUrl}>
                            {value}
                        </a>
                    ))}
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Branchen: </div>
                    <div>{(kunde.data.brancheList || []).map((nr) => branchenByNr[nr]?.text || nr).join(', ')}</div>
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Interesse für Produkte: </div>
                    <div>
                        {(kunde.data.interesseFuerProdukteList || [])
                            .map((nr) => interesseFuerProdukteByNr[nr]?.text || nr)
                            .join(', ')}
                    </div>
                </div>
                <div className={classnames(styles.line, styles.valueList)}>
                    <div>Erweiterte Suche: </div>
                    <div>
                        {(kunde.data.erweiterteSucheList || [])
                            .map((nr) => erweiterteSuchenByNr[nr]?.text || nr)
                            .join(', ')}
                    </div>
                </div>
                <div className={classnames(styles.line, styles.umsaetze)}>
                    <div>Umsätze: </div>
                    {kunde.data.umsatzLfd ? (
                        <div>
                            <strong>{formatMoney(kunde.data.umsatzLfd)}</strong> ({thisYear})
                        </div>
                    ) : null}
                    {kunde.data.umsatzVj1 ? (
                        <div>
                            <strong>{formatMoney(kunde.data.umsatzVj1)}</strong> ({thisYear - 1})
                        </div>
                    ) : null}
                    {kunde.data.umsatzVj2 ? (
                        <div>
                            <strong>{formatMoney(kunde.data.umsatzVj2)}</strong> ({thisYear - 2})
                        </div>
                    ) : null}
                    {kunde.data.umsatzVj3 ? (
                        <div>
                            <strong>{formatMoney(kunde.data.umsatzVj3)}</strong> ({thisYear - 3})
                        </div>
                    ) : null}
                </div>
                <div className={classnames(styles.line, styles.umsaetze)}>
                    <div>Offene Rechnungen: </div>
                    {kunde.data.mahnstufe0 ? (
                        <div>
                            <strong>{formatMoney(kunde.data.mahnstufe0)}</strong> (Mahnstufe 0)
                        </div>
                    ) : null}
                    {kunde.data.mahnstufe1 ? (
                        <div>
                            <strong>{formatMoney(kunde.data.mahnstufe1)}</strong> (Mahnstufe 1)
                        </div>
                    ) : null}
                    {kunde.data.mahnstufe2 ? (
                        <div>
                            <strong>{formatMoney(kunde.data?.mahnstufe2)}</strong> (Mahnstufe 2)
                        </div>
                    ) : null}
                    {kunde.data.mahnstufe3 ? (
                        <div>
                            <strong>{formatMoney(kunde.data?.mahnstufe3)}</strong> (Mahnstufe 3)
                        </div>
                    ) : null}
                </div>
                {kunde.data.bemerkung && <div className={styles.note}>{kunde.data.bemerkung}</div>}
            </div>
        );
    };

    const renderAngebote = () => {
        return (
            <div className={classnames(styles.group, { 'no-print': angeboteVisibleCount === 0 })}>
                <h1>Angebote</h1>
                {renderCounters((kunde.data?.angebotList || []).length, angeboteVisibleCount, setAngeboteVisibleCount)}
                {angeboteVisibleCount > 0 && (
                    <table className={styles.positionenTable}>
                        <thead>
                            <tr>
                                <td className={styles.tdHead}>Pos</td>
                                <td className={styles.tdHead}>Artikelgruppe</td>
                                <td className={styles.tdHead}>Artikel Nr.</td>
                                <td className={styles.tdHead}>Text</td>
                                <td className={styles.tdHead}>Einheit</td>
                                <td className={moneyHeadClassName}>Menge</td>
                                <td className={moneyHeadClassName}>EK Preis</td>
                                <td className={moneyHeadClassName}>Preis/Einh.</td>
                                <td className={moneyHeadClassName}>Preis (Netto)</td>
                            </tr>
                        </thead>
                        <tbody>
                            {(kunde.data?.angebotList || [])
                                .sort((a, b) => b.nr.localeCompare(a.nr))
                                .slice(0, angeboteVisibleCount)
                                .map((angebot) => {
                                    const positionen = (kunde.data?.angebotPositionList || [])
                                        .filter((position) => position.aNr === angebot.nr)
                                        .sort((a, b) => a.pos - b.pos);

                                    return (
                                        <>
                                            <tr>
                                                <td className={styles.tdGroupHeader}>{angebot.nr}</td>
                                                <td className={styles.tdGroupHeader}>{formatSmDate(angebot.datum)}</td>
                                                <td colSpan={7} />
                                            </tr>
                                            {positionen.map((position) => (
                                                <tr>
                                                    <td className={styles.tdBody}>{position.pos}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.artikelGruppeNr
                                                            ? artikelGruppenByNr[position.artikelGruppeNr]?.text ||
                                                              position.artikelGruppeNr
                                                            : ''}
                                                    </td>
                                                    <td className={styles.tdBody}>{position.artikelNr}</td>
                                                    <td className={styles.tdBody}>{position.posText}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.posEinh
                                                            ? einheitenByCode[position.posEinh].text || position.posEinh
                                                            : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posMenge ? position.posMenge.toFixed(2) : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEkPreis ? formatMoney(position.posEkPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEPreis ? formatMoney(position.posEPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posNetto ? formatMoney(position.posNetto) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td colSpan={8} />
                                                <td className={styles.tdGroupHeader}>{formatMoney(angebot.sNetto)}</td>
                                            </tr>
                                        </>
                                    );
                                })}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    const renderAuftraege = () => {
        return (
            <div className={classnames(styles.group, { 'no-print': auftraegeVisibleCount === 0 })}>
                <h1>Aufträge</h1>
                {renderCounters(
                    (kunde.data?.auftragList || []).length,
                    auftraegeVisibleCount,
                    setAuftraegeVisibleCount,
                )}
                {auftraegeVisibleCount > 0 && (
                    <table className={styles.positionenTable}>
                        <thead>
                            <tr>
                                <td className={styles.tdHead}>Pos</td>
                                <td className={styles.tdHead}>Artikelgruppe</td>
                                <td className={styles.tdHead}>Artikel Nr.</td>
                                <td className={styles.tdHead}>Text</td>
                                <td className={styles.tdHead}>Einheit</td>
                                <td className={moneyHeadClassName}>Menge</td>
                                <td className={moneyHeadClassName}>EK Preis</td>
                                <td className={moneyHeadClassName}>Preis/Einh.</td>
                                <td className={moneyHeadClassName}>Preis (Netto)</td>
                            </tr>
                        </thead>
                        <tbody>
                            {(kunde.data?.auftragList || [])
                                .sort((a, b) => b.nr.localeCompare(a.nr))
                                .slice(0, auftraegeVisibleCount)
                                .map((auftrag) => {
                                    const positionen = (kunde.data?.auftragPositionList || [])
                                        .filter((position) => position.auNr === auftrag.nr)
                                        .sort((a, b) => a.pos - b.pos);

                                    return (
                                        <>
                                            <tr>
                                                <td className={styles.tdGroupHeader}>{auftrag.nr}</td>
                                                <td className={styles.tdGroupHeader}>{formatSmDate(auftrag.datum)}</td>
                                                <td colSpan={7} />
                                            </tr>
                                            {positionen.map((position) => (
                                                <tr>
                                                    <td className={styles.tdBody}>{position.pos}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.artikelGruppeNr
                                                            ? artikelGruppenByNr[position.artikelGruppeNr]?.text ||
                                                              position.artikelGruppeNr
                                                            : ''}
                                                    </td>
                                                    <td className={styles.tdBody}>{position.artikelNr}</td>
                                                    <td className={styles.tdBody}>{position.posText}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.posEinh
                                                            ? einheitenByCode[position.posEinh].text || position.posEinh
                                                            : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posMenge ? position.posMenge.toFixed(2) : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEkPreis ? formatMoney(position.posEkPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEPreis ? formatMoney(position.posEPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posNetto ? formatMoney(position.posNetto) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td colSpan={8} />
                                                <td className={styles.tdGroupHeader}>{formatMoney(auftrag.sNetto)}</td>
                                            </tr>
                                        </>
                                    );
                                })}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    const renderRechnungen = () => {
        return (
            <div className={classnames(styles.group, { 'no-print': rechnungenVisibleCount === 0 })}>
                <h1>Rechnungen</h1>
                {renderCounters(
                    (kunde.data?.rechnungList || []).length,
                    rechnungenVisibleCount,
                    setRechnungenVisibleCount,
                )}
                {rechnungenVisibleCount > 0 && (
                    <table className={styles.positionenTable}>
                        <thead>
                            <tr>
                                <td className={styles.tdHead}>Pos</td>
                                <td className={styles.tdHead}>Artikelgruppe</td>
                                <td className={styles.tdHead}>Artikel Nr.</td>
                                <td className={styles.tdHead}>Text</td>
                                <td className={styles.tdHead}>Einheit</td>
                                <td className={moneyHeadClassName}>Menge</td>
                                <td className={moneyHeadClassName}>EK Preis</td>
                                <td className={moneyHeadClassName}>Preis/Einh.</td>
                                <td className={moneyHeadClassName}>Preis (Netto)</td>
                            </tr>
                        </thead>
                        <tbody>
                            {(kunde.data?.rechnungList || [])
                                .sort((a, b) => b.nr.localeCompare(a.nr))
                                .slice(0, rechnungenVisibleCount)
                                .map((rechnung) => {
                                    const positionen = (kunde.data?.rechnungPositionList || [])
                                        .filter((position) => position.rNr === rechnung.nr)
                                        .sort((a, b) => a.pos - b.pos);

                                    return (
                                        <>
                                            <tr>
                                                <td className={styles.tdGroupHeader}>{rechnung.nr}</td>
                                                <td className={styles.tdGroupHeader}>{formatSmDate(rechnung.datum)}</td>
                                                <td colSpan={7} />
                                            </tr>
                                            {positionen.map((position) => (
                                                <tr>
                                                    <td className={styles.tdBody}>{position.pos}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.artikelGruppeNr
                                                            ? artikelGruppenByNr[position.artikelGruppeNr]?.text ||
                                                              position.artikelGruppeNr
                                                            : ''}
                                                    </td>
                                                    <td className={styles.tdBody}>{position.artikelNr}</td>
                                                    <td className={styles.tdBody}>{position.posText}</td>
                                                    <td className={styles.tdBody}>
                                                        {position.posEinh
                                                            ? einheitenByCode[position.posEinh].text || position.posEinh
                                                            : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posMenge ? position.posMenge.toFixed(2) : '?'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEkPreis ? formatMoney(position.posEkPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posEPreis ? formatMoney(position.posEPreis) : '-'}
                                                    </td>
                                                    <td className={moneyBodyClassName}>
                                                        {position.posNetto ? formatMoney(position.posNetto) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td colSpan={8} />
                                                <td className={styles.tdGroupHeader}>{formatMoney(rechnung.sNetto)}</td>
                                            </tr>
                                        </>
                                    );
                                })}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    const renderAnsprechpartner = () => {
        return (
            <div className={styles.group}>
                <h1>Ansprechpartner</h1>
                <table className={styles.growTable}>
                    <thead>
                        <tr>
                            <td className={styles.tdHead}>Name</td>
                            <td className={styles.tdHead}>Funktion</td>
                            <td className={styles.tdHead}>Telefon</td>
                            <td className={styles.tdHead}>Mobil</td>
                            <td className={styles.tdHead}>Mail</td>
                        </tr>
                    </thead>
                    <tbody>
                        {(kunde.data?.ansprechpartnerList || [])
                            .sort((a, b) => b.name.localeCompare(a.name))
                            .map((ansprechpartner) => (
                                <tr>
                                    <td className={styles.tdBody}>
                                        {ansprechpartner.anrede &&
                                            (AnredeLabel[ansprechpartner.anrede] || ansprechpartner.anrede) + ' '}
                                        {ansprechpartner.titel && ansprechpartner.titel + ' '}
                                        {ansprechpartner.name && ansprechpartner.name + ' '}
                                    </td>
                                    <td className={styles.tdBody}>{ansprechpartner.position}</td>
                                    <td className={styles.tdBody}>{ansprechpartner.telefon}</td>
                                    <td className={styles.tdBody}>{ansprechpartner.mobil}</td>
                                    <td className={styles.tdBody}>{ansprechpartner.mail}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderMaschinenkunden = () => {
        return (
            <div className={styles.group}>
                <h1>Maschinen</h1>
                <table className={styles.growTable}>
                    <thead>
                        <tr>
                            <td className={styles.tdHead}>Hersteller</td>
                            <td className={styles.tdHead}>Maschine</td>
                            <td className={styles.tdHead}>Baujahr</td>
                            <td className={styles.tdHead}>Geräte-Nr.</td>
                            <td className={styles.tdHead}>Lieferdatum</td>
                            <td className={styles.tdHead}>Ausgeschieden seit</td>
                            <td className={styles.tdHead}>Bemerkung</td>
                        </tr>
                    </thead>
                    <tbody>
                        {(kunde.data?.maschinenkundeList || [])
                            .sort((a, b) => b.baujahr - a.baujahr)
                            .map((maschinenkunde) => (
                                <tr>
                                    <td className={styles.tdBody}>{maschinenkunde.hersteller}</td>
                                    <td className={styles.tdBody}>{maschinenkunde.type}</td>
                                    <td className={styles.tdBody}>{maschinenkunde.baujahr}</td>
                                    <td className={styles.tdBody}>{maschinenkunde.geraeteNr}</td>
                                    <td className={styles.tdBody}>{formatSmDate(maschinenkunde.lDat, true)}</td>
                                    <td className={styles.tdBody}>{formatSmDate(maschinenkunde.datAusgesch, true)}</td>
                                    <td className={styles.tdBody}>{maschinenkunde.bem}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderTermine = () => {
        return (
            <div className={classnames(styles.group, { 'no-print': termineVisibleCount === 0 })}>
                <h1>Kontakthistorie</h1>
                {renderCounters((kunde.data?.rechnungList || []).length, termineVisibleCount, setTermineVisibleCount)}
                {(kunde.data?.terminList || [])
                    .sort((a, b) => b.datumVon.localeCompare(a.datumVon))
                    .slice(0, termineVisibleCount)
                    .map((termin) => (
                        <div
                            className={classnames(styles.terminItemCell, { [formStyles.important]: termin?.prodInfo })}
                        >
                            <div className={styles.terminStatus}>
                                {termin?.terminStatus ? <CheckboxCompositeIcon /> : <CheckboxIcon />}
                            </div>
                            <div className={styles.terminKontakt}>
                                {termin?.userKz &&
                                    (betreuerByKz[termin?.userKz]
                                        ? betreuerByKz[termin?.userKz]?.name
                                        : termin?.userKz)}
                                {' - '}
                                {termin?.kaName || '?'}
                            </div>
                            <div className={styles.terminZeit}>
                                {termin?.datumVon && moment(termin?.datumVon).format('DD.MM.YYYY')}{' '}
                                {formatDecimalTime(termin?.zeitVon)}
                                {' - '}
                                {formatDecimalTime(termin?.zeitBis)}{' '}
                                {termin?.terminArt && (TerminArtLabel[termin.terminArt] || termin.terminArt)}
                            </div>
                            <div className={styles.terminBetreff}>{termin?.terminBetreff}</div>
                            <div className={styles.terminText}>{nl2br(termin?.terminText)}</div>
                            <div>{termin?.terminStatus}</div>
                        </div>
                    ))}
            </div>
        );
    };

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {kunde.isLoading && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            <h1 className={styles.title}>
                {kunde.data?.name1 || ''} {kunde.data?.name2 || ''}({kunde.data?.nr || ''})
            </h1>
            {printView && (
                <table className={formStyles.reportFilterInfo}>
                    <tr>
                        <td>Druckdatum: </td>
                        <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                    </tr>
                </table>
            )}
            {renderHead()}
            {renderAnsprechpartner()}
            {renderMaschinenkunden()}
            {renderTermine()}
            {renderAngebote()}
            {renderAuftraege()}
            {renderRechnungen()}
        </div>
    );
};
